import React, { useCallback, useMemo, useRef } from 'react';
import useChatAgent, { sumEstimatedCostUsd } from './useChatAgent';
import ChatThread from './ChatThread';
import ChatComposer from './ChatComposer';
import ChatLanguageMenu, { LANGUAGE_QUESTION_ID } from './ChatLanguageMenu';

/**
 * The same chat as the floating widget, embedded in a page.
 *
 * Same hook, same thread, same block renderer, same composer — what differs
 * is only the shell: no pill, no compact/expanded modes, no body-scroll lock,
 * and no ingest overlay, because a panel sitting inside a tab is not covering
 * anything that needs locking. A second copy of the chat would have been a
 * second thing to keep in step with every change to the first; this is the
 * first one with a different frame around it.
 *
 * TWO THINGS A CALLER MUST GET RIGHT:
 *
 * 1. `storageNamespace` is required. `useChatAgent` persists the session id
 *    and the block snapshot in localStorage, and the floating widget holds
 *    the un-suffixed keys. Two chats sharing them is not two conversations:
 *    they read each other's sessionId, overwrite each other's snapshots, and
 *    "New chat" in one empties the other.
 *
 * 2. `transformMessage` is how you scope the conversation. The agent's wire
 *    protocol is fixed — `{prompt}` to a runtime ARN with a session-id header
 *    — so there is no field to put a knowledge-base id in. Scoping therefore
 *    happens in the words of the prompt, and the panel shows the operator
 *    what they typed rather than the scoped text it sent (see the `display`
 *    option on `send`).
 */

const NO_EXAMPLES = [];

function fmtCost(usd) {
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(4)}`;
}

function ChatPanel({
  storageNamespace,
  title = 'Assistant',
  icon = 'fa-comments',
  /** Empty-state copy in the thread, before the first turn. */
  placeholder = 'Ask a question.',
  composerPlaceholder = 'Ask the assistant…',
  examples = NO_EXAMPLES,
  /** (text) => text actually sent. Identity when omitted. */
  transformMessage = null,
  /** Rendered under the header — e.g. "answers come from <KB name>". */
  subtitle = null,
  className = '',
}) {
  const threadRef = useRef(null);
  const {
    blocks, sessionId, status, error, pending, send, answer, decideApproval, reset,
  } = useChatAgent({ storageNamespace });

  const totalCostUsd = useMemo(() => sumEstimatedCostUsd(blocks), [blocks]);

  const handleSubmit = useCallback((text) => {
    threadRef.current?.scrollToLatest('auto');
    const scoped = transformMessage ? transformMessage(text) : text;
    // `display` keeps the scoping preamble out of the operator's own bubble.
    send(scoped, scoped === text ? undefined : { display: text });
  }, [send, transformMessage]);

  // Same wire shape as the one-time language question's own buttons, so
  // the server validates and stores it through the single path.
  const handleLanguage = useCallback((code) => {
    answer(LANGUAGE_QUESTION_ID, code);
  }, [answer]);

  return (
    <div className={`chat-widget-embedded ${className}`.trim()}>
      <div className="chat-widget-card-header">
        <div className="chat-widget-header-content">
          <div className="chat-widget-header-title">
            <i className={`fa ${icon} me-2 text-primary`} aria-hidden="true" />
            {title}
          </div>
          <div className="chat-widget-header-meta">
            <span
              className={`chat-widget-status-dot chat-widget-status-dot-${status}`}
              aria-hidden="true"
            />
            <span>{status === 'error' ? 'Connection failed' : 'Online'}</span>
            {subtitle && (
              <>
                <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                <span className="text-muted">{subtitle}</span>
              </>
            )}
            {sessionId && (
              <>
                <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                <button
                  type="button"
                  className="chat-widget-header-new"
                  onClick={reset}
                  title="Start a new conversation"
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  <span>New chat</span>
                </button>
                <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                <ChatLanguageMenu onChoose={handleLanguage} disabled={pending} />
              </>
            )}
            {totalCostUsd > 0 && (
              <>
                <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                <span
                  className="chat-widget-header-cost"
                  title={`Estimated cost this session: ~$${totalCostUsd.toFixed(6)} USD`}
                >
                  {fmtCost(totalCostUsd)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <ChatThread
        ref={threadRef}
        blocks={blocks}
        pending={pending}
        error={error}
        status={status}
        onAnswer={answer}
        onDecideApproval={decideApproval}
        placeholder={placeholder}
      />

      <ChatComposer
        onSubmit={handleSubmit}
        disabled={status !== 'connected' || pending}
        pending={pending}
        rows={2}
        placeholder={composerPlaceholder}
        examples={examples}
      />
    </div>
  );
}

export default ChatPanel;
